import type { CanvasAsset } from '@/entities/node/canvas-asset'
import type { Asset } from '@/entities/asset'
import { batchDownloadAssets } from '@/shared/lib/utils/batch-download'
import { isVip, useTeamState, aiWatermarkPreferenceStore } from '@/entities/team'
import { downloadRemoteFile } from '@/shared/lib/utils/download-utils'
import { appendWatermarkToOssUrl } from '@/shared/lib/watermark'
import { useCallback } from 'react'
import { useSnapshot } from 'valtio'

import { canvasAssetsStore } from '@/entities/node/canvas-asset'

function resolveIsMaterial(assetId?: string) {
  if (assetId) {
    const asset = canvasAssetsStore.assets.get(assetId)
    if (asset && (asset as any).material_id) { // material_id 有这个字段代表AI生成的素材
      return false
    }
  }

  return true
}

/**
 * 带有水印逻辑的下载 Hook
 * 会自动判断当前节点是否为素材节点，以及用户所在团队的 VIP 状态，
 * 对非素材节点的非 VIP 用户进行水印处理。
 */
export function useDownloadWithWatermark() {
  const { currentTeam } = useTeamState()
  const { serverAiWatermark } = useSnapshot(aiWatermarkPreferenceStore)

  return useCallback(async (imageUrl: string, fallbackBaseName = 'image', assetId?: string) => {
    if (!imageUrl) return

    const isMaterial = resolveIsMaterial(assetId)
    
    // 获取源素材的真实宽度，用于传给水印工具类进行动态计算自适应缩小比例
    let targetWidth: number | undefined
    if (assetId) {
      const asset = canvasAssetsStore.assets.get(assetId)
      targetWidth = asset?.width
    }

    let downloadUrl = imageUrl
    if (!isMaterial) {
      downloadUrl = appendWatermarkToOssUrl(imageUrl, targetWidth, serverAiWatermark !== 0, isVip(currentTeam!))
    }

    await downloadRemoteFile(downloadUrl, fallbackBaseName)
  }, [currentTeam])
}

/**
 * 带有水印逻辑的图集批量下载 Hook
 */
export function useDownloadGalleryAssetsWithWatermark() {
  const { currentTeam } = useTeamState()
  const { serverAiWatermark } = useSnapshot(aiWatermarkPreferenceStore)

  return useCallback(async (assets: ReadonlyArray<CanvasAsset | undefined>) => {
    const isUserVip = isVip(currentTeam!)

    const watermarkedAssets = assets.map(asset => {
      if (!asset) return asset
      const imageUrl = asset.url || asset.previewUrl || ''
      if (!imageUrl) return asset

      const isMaterial = resolveIsMaterial((asset as any)._assetId || asset.id)

      if (!isMaterial) {
        return {
          ...asset,
          url: appendWatermarkToOssUrl(imageUrl, asset.width, serverAiWatermark !== 0, isUserVip)
        }
      }
      return asset
    })

    return batchDownloadAssets(watermarkedAssets)
  }, [currentTeam])
}

/**
 * 专用于素材库的单文件下载 Hook，包含水印逻辑
 * AI生成的素材（source === 'generated'）如果是非图片或者会员，则不加水印。
 */
export function useAssetLibraryDownloadWithWatermark() {
  const { currentTeam } = useTeamState()
  const { serverAiWatermark } = useSnapshot(aiWatermarkPreferenceStore)

  return useCallback(async (asset: Asset, fallbackBaseName = 'image') => {
    let downloadUrl = asset.originalUrl || asset.previewUrl || ''
    if (!downloadUrl) return

    // 如果是 AI 生成的，则视为需要打水印的范畴
    const isAiGenerated = asset.source === 'generated'
    const isImage = asset.type === 'image'

    if (isAiGenerated && isImage) {
      downloadUrl = appendWatermarkToOssUrl(downloadUrl, asset.width, serverAiWatermark !== 0, isVip(currentTeam!))
    }

    await downloadRemoteFile(downloadUrl, asset.title || fallbackBaseName)
  }, [currentTeam])
}

/**
 * 专用于素材库的批量下载 Hook，包含水印逻辑
 */
export function useAssetLibraryBatchDownloadWithWatermark() {
  const { currentTeam } = useTeamState()
  const { serverAiWatermark } = useSnapshot(aiWatermarkPreferenceStore)

  return useCallback(async (assets: Asset[]) => {
    const isUserVip = isVip(currentTeam!)

    const watermarkedAssets = assets.map(asset => {
      const url = asset.originalUrl || asset.previewUrl || ''
      if (!url) return asset

      const isAiGenerated = asset.source === 'generated'
      const isImage = asset.type === 'image'

      if (isAiGenerated && isImage) {
        return {
          ...asset,
          // downloadGalleryAssets 底层读取 url 或者 previewUrl，强转并覆盖
          url: appendWatermarkToOssUrl(url, asset.width, serverAiWatermark !== 0, isUserVip),
          previewUrl: appendWatermarkToOssUrl(url, asset.width, serverAiWatermark !== 0, isUserVip),
        }
      }
      return { ...asset, url }
    })

    // 因为底层 batchDownloadAssets 已经提取出公共的 DownloadableAsset 类型，可直接传入
    return batchDownloadAssets(watermarkedAssets)
  }, [currentTeam])
}

